import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { type MouseEvent, type ReactNode } from "react";
import { ArrowRight } from "lucide-react";

import { useTrack } from "@/analytics/events";
import PurchaseAssuranceIcon from "@/blueprint/components/PurchaseAssuranceIcon/PurchaseAssuranceIcon";
import { NAV_SHOP_PANEL, type NavShopProduct } from "@/chrome/chromeContent";
import NavPanelRow from "@/chrome/SiteNav/NavPanelRow";
import { cn } from "@/lib/utils";

// Shop content shared by the desktop nav hover panel and the mobile menu's
// shop sheet — same copy, different geometry per Figma Desktop/Mobile Nav.
export type NavShopContentVariant = "panel" | "sheet";

type VariantClasses = {
  root: string;
  graphicCol: string;
  icon: string;
  arrowBox: string;
  buyImg: string;
  accessoriesImg: string;
  orderStatusImg: string;
  assuranceRow: string;
  assuranceCol: string;
  title: string;
  detail: string;
};

const VARIANT_CLASSES: Record<NavShopContentVariant, VariantClasses> = {
  panel: {
    root: "gap-xl px-xl pb-xl pt-[60px]",
    graphicCol: "w-[146px]",
    icon: "size-xl",
    arrowBox: "size-xxl",
    buyImg: "h-[47px] w-[93px]",
    /* Matches the render's own 1.364 ratio, so object-contain fills the box
       instead of letterboxing it to 53px wide. */
    accessoriesImg: "h-[64px] w-[87px]",
    orderStatusImg: "h-[60px] w-[83px] rounded-card object-cover",
    assuranceRow: "gap-xl",
    assuranceCol: "gap-xs",
    title: "text-paragraph-md",
    detail: "text-paragraph-sm",
  },
  sheet: {
    root: "gap-l pb-xl",
    graphicCol: "w-[80px]",
    icon: "size-l",
    arrowBox: "size-xl",
    buyImg: "h-[31px] w-[61px]",
    accessoriesImg: "h-[46px] w-[63px]",
    orderStatusImg: "h-[42px] w-[58px] rounded-card object-cover",
    // Figma tiles four fixed 82px columns edge to edge (Mobile Nav
    // State=Shop); equal quarters reproduce that at every sheet width.
    assuranceRow: "gap-0",
    assuranceCol: "gap-[4px]",
    // The sheet is one fixed composition across mobile and tablet (Figma
    // Mobile Nav State=Shop), so its type does not ride the responsive scale.
    title: "text-nav-label",
    detail: "text-nav-label-sm",
  },
};

type ProductRowProps = {
  product: NavShopProduct;
  imageClasses: string;
  variant: VariantClasses;
  onNavigate: () => void;
};

const ProductRow = ({
  product,
  imageClasses,
  variant,
  onNavigate,
}: ProductRowProps) => {
  const { asPath } = useRouter();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onNavigate();
    // A same-route SPA transition reconciles to nothing, so the click reads
    // as dead. Hand it to the browser instead — a real document navigation
    // with its visible refresh (apple.com's logo behavior). Modified clicks
    // keep their browser behavior: new tab, new window.
    const samePage = asPath.split(/[?#]/)[0] === product.href;
    const plainLeftClick =
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey;
    if (samePage && plainLeftClick) {
      event.preventDefault();
      window.location.assign(product.href);
    }
  };

  return (
    <Link
      href={product.href}
      onClick={handleClick}
      className="flex items-center"
    >
      <div
        className={cn(
          "flex h-[50px] shrink-0 items-center",
          variant.graphicCol,
        )}
      >
        <Image
          src={product.image}
          alt=""
          className={cn("object-contain", imageClasses)}
        />
      </div>
      <div className="flex flex-1 items-center justify-between">
        <div className="flex flex-col gap-[2px]">
          <p
            className={cn("font-semibold text-content-primary", variant.title)}
          >
            {product.title}
          </p>
          <p
            className={cn(
              "flex items-center gap-s text-content-secondary",
              variant.detail,
            )}
          >
            {product.meta}
          </p>
        </div>
        <span
          className={cn(
            "flex items-center justify-center text-content-primary",
            variant.arrowBox,
          )}
        >
          <ArrowRight aria-hidden className="size-l" strokeWidth={1.15} />
        </span>
      </div>
    </Link>
  );
};

type NavShopContentProps = {
  variant: NavShopContentVariant;
  onNavigate: () => void;
  /** Panel surface only: drives the row stagger. The mobile sheet leaves this
   *  unset and renders its rows plainly — the sheet has its own entrance. */
  panelOpen?: boolean | undefined;
  reducedMotion?: boolean | undefined;
  panelWasClosed?: boolean | undefined;
};

const NavShopContent = ({
  variant,
  onNavigate,
  panelOpen,
  reducedMotion = false,
  panelWasClosed = false,
}: NavShopContentProps) => {
  const classes = VARIANT_CLASSES[variant];
  const track = useTrack();
  // These two rows are the only commercial clicks in the nav, and they were
  // unmeasured on both surfaces. The variant is the surface: the same content
  // renders in the desktop hover panel and in the mobile sheet.
  const navigateTo = (product: NavShopProduct, index: number) => {
    track("nav_link_clicked", {
      href: product.href,
      label: product.title,
      surface: variant === "sheet" ? "menu_sheet" : "shop_panel",
      nav_link_index: index,
    });
    onNavigate();
  };

  // Unset on the sheet, so its rows stay plain elements with no motion state.
  const row = (index: number, children: ReactNode) =>
    panelOpen === undefined ? (
      children
    ) : (
      <NavPanelRow
        index={index}
        open={panelOpen}
        reducedMotion={reducedMotion}
        panelWasClosed={panelWasClosed}
      >
        {children}
      </NavPanelRow>
    );

  return (
    <div className={cn("flex w-full flex-col font-ppMori", classes.root)}>
      {row(
        0,
        <ProductRow
          product={NAV_SHOP_PANEL.buy}
          imageClasses={classes.buyImg}
          variant={classes}
          onNavigate={() => navigateTo(NAV_SHOP_PANEL.buy, 0)}
        />,
      )}
      {row(
        1,
        <div className={cn("flex", classes.assuranceRow)}>
          {NAV_SHOP_PANEL.assurances.map((assurance) => {
            return (
              <div
                key={assurance.title}
                /* basis-0 + min-w-0 is what makes the quarters equal: a flex
                 item's automatic minimum size otherwise lets the longest
                 label hold its column open and squeeze its neighbours. */
                className={cn(
                  "flex min-w-0 flex-1 basis-0 flex-col items-center pt-s text-center",
                  classes.assuranceCol,
                )}
              >
                <div
                  className={cn(
                    "flex items-center justify-center",
                    classes.icon,
                  )}
                >
                  <PurchaseAssuranceIcon
                    icon={assurance.icon}
                    className="size-full text-content-primary"
                  />
                </div>
                <p
                  className={cn(
                    "whitespace-pre-line text-content-primary",
                    classes.detail,
                  )}
                >
                  {assurance.title}
                </p>
              </div>
            );
          })}
        </div>,
      )}
      {row(2, <div className="border-t border-neutral1" />)}
      {row(
        3,
        <ProductRow
          product={NAV_SHOP_PANEL.accessories}
          imageClasses={classes.accessoriesImg}
          variant={classes}
          onNavigate={() => navigateTo(NAV_SHOP_PANEL.accessories, 1)}
        />,
      )}
      {row(4, <div className="border-t border-neutral1" />)}
      {row(
        5,
        <ProductRow
          product={NAV_SHOP_PANEL.orderStatus}
          imageClasses={classes.orderStatusImg}
          variant={classes}
          onNavigate={() => navigateTo(NAV_SHOP_PANEL.orderStatus, 2)}
        />,
      )}
    </div>
  );
};

export default NavShopContent;
